import { useState, useRef } from "react";
import { FileCategory } from "../components/FileCategoryModal";
import {
  getPresignedUrl,
  uploadFileToPresignedUrl,
} from "../service/uploadFileService";
import { useToast } from "@customHooks/useToast";

interface CategorizedFiles {
  [key: string]: {
    files: File[];
    previews: string[];
    progress: number[];
    uploadStatus: string[];
    notificationShown: boolean[]; // Add notification flag
  };
}

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const useFileUpload = ({
  applicationId
}: {
  applicationId?: string;
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<FileCategory | null>(
    null
  );
  const [categorizedFiles, setCategorizedFiles] = useState<CategorizedFiles>(
    {}
  );

  const handleUploadSectionClick = () => {
    setShowModal(true);
  };

  const handleCategorySelect = (category: FileCategory) => {
    setSelectedCategory(category);
    setShowModal(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && selectedCategory) {
      const newFiles = Array.from(files).map((file) => {
        const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
        const fileExtension = file.name.split(".").pop(); // Extract the file extension
        const baseName = file.name.slice(0, file.name.lastIndexOf(".")); // Get the base name of the file
        const newFileName = `${selectedCategory}_${timestamp}_${baseName}.${fileExtension}`; // Append the extension
        const renamedFile = new File([file], newFileName, { type: file.type });
        return renamedFile;
      });

      const newPreviews: string[] = [];
      const newProgress: number[] = [];
      const newUploadStatus: string[] = [];
      const newNotificationFlags: boolean[] = []; // Add array to track notifications

      newFiles.forEach((file) => {

        if (file.size > MAX_FILE_SIZE) {
          toast({
            title: "File too large",
            description: `${file.name} exceeds the 10MB size limit.`
          });

          return;
        }

        newProgress.push(0);
        newUploadStatus.push("uploading");
        newNotificationFlags.push(false); // Initially set to false, meaning no notification shown

        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push(reader.result as string);
            setCategorizedFiles((prev) => ({
              ...prev,
              [selectedCategory]: {
                files: [...(prev[selectedCategory]?.files || []), file],
                previews: [
                  ...(prev[selectedCategory]?.previews || []),
                  ...newPreviews,
                ],
                progress: [
                  ...(prev[selectedCategory]?.progress || []),
                  ...newProgress,
                ],
                uploadStatus: [
                  ...(prev[selectedCategory]?.uploadStatus || []),
                  ...newUploadStatus,
                ],
                notificationShown: [
                  ...(prev[selectedCategory]?.notificationShown || []),
                  ...newNotificationFlags,
                ], // Add the notification flags
              },
            }));
          };
          reader.readAsDataURL(file);
        } else {
          setCategorizedFiles((prev) => ({
            ...prev,
            [selectedCategory]: {
              files: [...(prev[selectedCategory]?.files || []), file],
              previews: [
                ...(prev[selectedCategory]?.previews || []),
                "non-image",
              ],
              progress: [
                ...(prev[selectedCategory]?.progress || []),
                ...newProgress,
              ],
              uploadStatus: [
                ...(prev[selectedCategory]?.uploadStatus || []),
                ...newUploadStatus,
              ],
              notificationShown: [
                ...(prev[selectedCategory]?.notificationShown || []),
                ...newNotificationFlags,
              ], // Add the notification flags
            },
          }));
        }
      });
    }
  };

  const handleFileUpload = async (index: number, category: FileCategory) => {
    try {
      // Ensure file is defined from the state
      const file = categorizedFiles[category]?.files[index];
      if (!file || !selectedCategory) return;

      if (!applicationId) {
        throw new Error("Application ID not found");
      }

      // Request a presigned URL if applicationId exists
      const uploadRequest = {
        filename: file.name, // Ensure file is properly referenced here
        applicationId: applicationId,
        documentType: selectedCategory,
      };
      const presignedUrlData = await getPresignedUrl(uploadRequest);

      // Upload the file to the presigned URL with progress tracking
      await uploadFileToPresignedUrl(
        file,
        presignedUrlData.fileUploadUrl,
        (progress: number) => {
          setCategorizedFiles((prev) => {
            const updatedProgress = [...prev[category]?.progress];
            updatedProgress[index] = progress;
            return {
              ...prev,
              [category]: {
                ...prev[category],
                progress: updatedProgress,
              },
            };
          });
        }
      );

      // Update the status of the file upload
      setCategorizedFiles((prev) => {
        const updatedStatus = [...prev[category]?.uploadStatus];
        updatedStatus[index] = "success"; // Set the upload status to success for this file
        const updatedNotificationFlags = [...prev[category]?.notificationShown];
        updatedNotificationFlags[index] = true; // Set notification flag to true after success
        return {
          ...prev,
          [category]: {
            ...prev[category],
            uploadStatus: updatedStatus,
            notificationShown: updatedNotificationFlags,
          },
        };
      });

      // Show notification (example)
      if (!categorizedFiles[category]?.notificationShown[index]) {
        toast({
          title: "Upload Successful",
          description: `${file.name} has been uploaded successfully.`,
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setCategorizedFiles((prev) => {
        const updatedStatus = [...prev[category]?.uploadStatus];
        updatedStatus[index] = "failed";
        const updatedNotificationFlags = [...prev[category]?.notificationShown];
        updatedNotificationFlags[index] = true;
        return {
          ...prev,
          [category]: {
            ...prev[category],
            uploadStatus: updatedStatus,
            notificationShown: updatedNotificationFlags,
          },
        };
      });
    }
  };

  const openImageModal = (image: string) => {
    setModalImage(image);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImage(null);
  };

  return {
    fileInputRef,
    showModal,
    setShowModal,
    showImageModal,
    modalImage,
    selectedCategory,
    categorizedFiles,
    handleUploadSectionClick,
    handleCategorySelect,
    handleFileChange,
    handleFileUpload,
    openImageModal,
    closeImageModal,
    setShowImageModal,
  };
};
